<template>
  <ul
    :id="'sb-combobox-options-' + inputId"
    :anchor="'sb-combobox-wrapper-' + inputId"
    :aria-labelledby="'sb-combobox-button-' + inputId"
    :style="`position-anchor: --dialog-${inputId};`"
    class="sb-select-list"
    role="listbox"
    tabindex="0"
    @keydown="handleKeyDown"
  >
    <template v-if="hasOptions">
      <SbSelectListItem
        v-for="(option, index) in options"
        :key="index"
        :option="option"
        :label="option[itemLabel]?.toString()"
        :model-value="option[itemValue]"
        :item-value="itemValue"
        :input-value="modelValue"
        :emit-option="emitOption"
        :use-avatars="useAvatars"
        :show-caption="showCaption"
        :details="option[itemCaption]?.toString()"
        :selected="isSelected(index)"
        :is-disabled="isOptionDisabled(option)"
        :data-testid="`${dataTestid}-item__${index}`"
        :input-id="inputId"
        :navigational-select="navigationalSelect"
        @emit-value="handleEmitValue"
      >
        <template #list-item="scope">
          <slot name="list-item" v-bind="scope" />
        </template>
      </SbSelectListItem>
    </template>
    <li
      v-if="isLoadingMore"
      class="sb-select-list__loading"
      :data-testid="`${dataTestid}-loading-more`"
    >
      <SbLoading color="primary" size="normal" />
      {{ loadingMoreText }}
    </li>
    <li
      v-else-if="!hasOptions && !isLoading"
      :data-testid="`${dataTestid}-no-data-text`"
      class="sb-select-list__empty"
    >
      {{ noDataText }}
    </li>
  </ul>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import SbLoading from '../../Loading'

import { useSelectContext } from '../helpers'
import type { Option, SelectValue } from '../types'
import SbSelectListItem from './SelectListItem.vue'
import { Tooltip } from '../../../directives'

export default {
  name: 'SbSelectList',

  components: {
    SbSelectListItem,
    SbLoading,
  },

  directives: {
    Tooltip,
  },

  props: {
    // input itself value
    modelValue: {
      type: [String, Number] as PropType<SelectValue>,
      default: null,
    },

    // options
    emitOption: Boolean,
    isLoading: Boolean,
    options: {
      type: Array as PropType<Option[]>,
      required: true,
      default: () => [],
    },
    useAvatars: Boolean,
    itemLabel: {
      type: String,
      default: 'label',
    },
    inputId: {
      type: String,
      default: '',
    },
    itemValue: {
      type: String,
      default: 'value',
    },
    noDataText: {
      type: String,
      required: true,
    },
    searchInput: {
      type: String,
      default: undefined,
    },

    showCaption: Boolean,
    itemCaption: {
      type: String,
      default: 'path',
    },

    isLoadingMore: Boolean,
    loadingMoreText: {
      type: String,
      required: true,
    },
    isOptionDisabled: {
      type: Function,
      default: () => false,
    },
    navigationalSelect: Boolean,
  },

  emits: ['emit-value', 'keydown'],

  setup() {
    const selectContext = useSelectContext()

    return {
      selectContext,
    }
  },

  computed: {
    dataTestid() {
      return this.$attrs['data-testid'] || 'sb-select-list'
    },

    context() {
      return this.selectContext()
    },

    hasOptions() {
      return this.options.length
    },
  },

  methods: {
    /**
     * forward the 'emit-value' event
     * @param {String} value
     */
    handleEmitValue(value: SelectValue) {
      this.$emit('emit-value', value)
    },

    /**
     * handle with keydown event when list component is focused
     * @param {Event} event
     */
    handleKeyDown(event: KeyboardEvent) {
      const {
        activeIndex,
        focusAtIndex,
        focusOnFirstItem,
        focusOnLastItem,
        hideList,
        listItems,
      } = this.context
      const count = listItems?.length || 0

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          focusAtIndex((activeIndex + 1) % count)
          break
        case 'ArrowUp':
          event.preventDefault()
          focusAtIndex((activeIndex - 1 + count) % count)
          break
        case 'Home':
          focusOnFirstItem()
          break
        case 'End':
          focusOnLastItem()
          break
        case 'Tab':
          hideList()
          break
        case 'Escape':
          hideList()
          break
        default:
          break
      }

      this.$emit('keydown', event)
    },

    isSelected(index: number) {
      return this.context?.selectedItemIndex === index
    },
  },
}
</script>
